// Settings: the Director's voice and the default narrator, each with a sample to hear and voice auditions to pick
// from (the same audition_voices / voice_sample / set_director_voice tools the Director uses by voice), plus the
// account and, for admins, the way in to People, Invites, Costs and Feedback. v4: Spending (V4-SP) limits.
// v4 (V4-P2): every section but the account is a Fold with a one-line summary; "Your Director" starts open.
import { useState, type FormEvent } from "react";
import { tapTool } from "../actions";
import { api, call } from "../api";
import { toggleAudio } from "../audio";
import { ArtifactCard } from "../components/Artifact";
import { DirectorPicker, usePersona } from "../components/DirectorPicker";
import { HelpAndData } from "../components/HelpAndData";
import { Icon, Spinner, useLoad } from "../components/bits";
import { Fold } from "../components/Fold";
import { MemoryPanel } from "../components/MemoryPanel";
import { ModelDefaults } from "../components/ModelDefaults";
import { Shell } from "../components/Shell";
import { SpendingPanel } from "../components/SpendingPanel";
import { VoiceSearch } from "../components/VoiceSearch";
import { director } from "../director";
import { channelSettingsPath, Link, navigate } from "../router";
import { get as storeGet, set, toast, useRefresh, useStore } from "../store";
import { loadSpending } from "../spending";
import { limitsSummary } from "../foldtext";
import type { Artifact } from "../types";
import { SafetyBadge, useSafetyCount } from "../components/SafetyBadge";
import { YoutubeConnection } from "../components/YouTube";
import { youtube } from "../youtube";

/** The agent's default Director voice (spec-v2-video.md: Eric, smooth and calm) when the user hasn't picked one. */
export const DEFAULT_DIRECTOR_VOICE = { voiceId: "cjVigY5qzO86Huf0OWal", name: "Eric" };

const DIRECTOR_ASKS = ["Calmer", "Warmer", "Older", "Lower", "British", "A woman's voice"];
const NARRATOR_ASKS = ["Warm and friendly", "Deep and trustworthy", "Young and upbeat", "Calm woman", "British", "Older man"];

export function SettingsScreen() {
  const me = useStore((s) => s.me);
  const safety = useSafetyCount(me?.user.role === "admin", true); // Wave 1 safety: the count on the Safety row
  const brand = me?.channels[0];
  const persona = usePersona();
  const status = useStore((s) => s.director);
  // the voice your Director speaks in: your pick, else its character's own (Key Light Director)
  const directorVoice = { voiceId: persona.voiceId, name: persona.voiceName ?? "Your pick" };
  const narrator = brand?.brand.narrator ?? null;
  // the folds' one-line summaries (the panels load their own full lists)
  const tick = useRefresh("credits", "spending", "memory");
  const { data: memories } = useLoad(() => api.memory().catch(() => null), [tick]);
  const { data: spending } = useLoad(() => loadSpending().catch(() => null), [tick]);
  const { data: youtubeStatus, reload: reloadYoutube } = useLoad(() => youtube.status(), []);
  return (
    <Shell>
      <div className="page settings">
        <header className="page-head">
          <h1 className="page-title">Settings</h1>
          <p className="page-lede">How your Director sounds, who reads your videos, and what it remembers about you. You can also just ask: “try a calmer voice”, “find me an older narrator”.</p>
        </header>

        <div className="settings-grid settings-folds">
          <Fold id="settings.director" title="Your Director" summary={persona.name} defaultOpen holds={["#dchar-h"]} className="set-fold">
            <section className="group settings-director" aria-labelledby="dchar-h">
              <h2 id="dchar-h" className="group-title">
                Your Director
              </h2>
              <p className="muted">Who you talk to. Each Director is a different film light with its own voice. Rename it, or give it any voice you like.</p>
              <DirectorPicker status={status} />
            </section>
          </Fold>

          <Fold id="settings.voice" title="Director voice" summary={persona.customVoice ? directorVoice.name : `${persona.name}’s own voice`} holds={["settings.director-voice"]} className="set-fold">
            <VoicePicker
              who="director"
              title="Find a Director voice"
              lede={`Describe a voice and hear the closest few. Picking one gives ${persona.name} that voice; during a call it switches over in a second or two and picks up where you were.`}
              current={directorVoice ?? DEFAULT_DIRECTOR_VOICE}
              currentNote={persona.customVoice ? undefined : `${persona.name}’s own voice`}
              sampleText={`Hi${me ? `, ${me.user.name.split(" ")[0]}` : ""}. This is how I'll sound while we make your videos together.`}
              asks={DIRECTOR_ASKS}
              help="settings.director-voice"
            />
          </Fold>

          <Fold id="settings.narrator" title="Default narrator" summary={narrator?.name ?? "Your Director picks one"} holds={["settings.narrator"]} className="set-fold">
            <VoicePicker
              who="narrator"
              title="Default narrator"
              lede={brand ? `Reads the voiceover on new ${brand.name} videos. Any video can still use someone else.` : "Reads the voiceover on new videos. Set up a brand to keep a default."}
              current={narrator}
              currentNote={narrator ? undefined : "Your Director picks one per ad"}
              sampleText={brand?.brand.tagline || "Fresh from the oven every Saturday. Pre-order by Thursday."}
              asks={NARRATOR_ASKS}
              help="settings.narrator"
            />
          </Fold>

          <Fold id="settings.memory" title="Memory" count={memories?.length || undefined} summary={memories && !memories.length ? "Nothing remembered yet" : "What your Director remembers"} holds={["#mem-h"]} className="set-fold">
            <MemoryPanel />
          </Fold>

          <Fold id="settings.models" title="Models" summary="What each step is made with" holds={["#models-h"]} className="set-fold">
            <ModelDefaults />
          </Fold>

          <Fold id="settings.spending" title="Spending" summary={spending ? limitsSummary(spending) : "Limits you set yourself"} holds={["settings.spending"]} className="set-fold">
            <SpendingPanel />
          </Fold>

          <Fold id="settings.notifications" title="Notifications" summary={me?.user.emailVideoReady === false ? "No emails when a video is ready" : "An email when a video is ready"} className="set-fold">
            <NotificationsPanel on={me?.user.emailVideoReady !== false} />
          </Fold>

          {/* Wave 5: connected accounts (YouTube); hidden when the server has none switched on */}
          {youtubeStatus?.enabled && (
            <Fold id="settings.connections" title="Connections" summary={youtubeStatus.connected ? "YouTube connected" : "Connect YouTube to upload"} className="set-fold">
              <YoutubeConnection status={youtubeStatus} reload={reloadYoutube} />
            </Fold>
          )}

          <Fold id="settings.help" title="Help and your data" summary="A problem, an idea, a copy of your data" holds={["settings.help"]} className="set-fold">
            <HelpAndData />
          </Fold>

          <section className="group settings-account" aria-labelledby="acct-h">
            <h2 id="acct-h" className="group-title">
              Account
            </h2>
            {me && (
              <p className="settings-me">
                <span className="avatar" aria-hidden>
                  {me.user.name.slice(0, 1).toUpperCase()}
                </span>
                <span>
                  <strong>{me.user.name}</strong>
                  <span className="muted">{me.user.email}</span>
                </span>
              </p>
            )}
            {me?.user.role === "admin" && (
              <Link to="/admin/people" className="settings-row">
                <Icon name="person" />
                <span>
                  <strong>People</strong>
                  <span className="muted">Let people in, give credits, pause an account</span>
                </span>
                <Icon name="back" size={16} />
              </Link>
            )}
            {me?.user.role === "admin" && (
              <Link to="/admin/invites" className="settings-row">
                <Icon name="ticket" />
                <span>
                  <strong>Invites</strong>
                  <span className="muted">Make invite links, see who joined, revoke a link</span>
                </span>
                <Icon name="back" size={16} />
              </Link>
            )}
            {me?.user.role === "admin" && (
              <Link to="/admin/safety" className="settings-row">
                <Icon name="shield" />
                <span>
                  <strong>
                    Safety <SafetyBadge count={safety} />
                  </strong>
                  <span className="muted">Blocked uploads and prompts, flags, account locks and the consent log</span>
                </span>
                <Icon name="back" size={16} />
              </Link>
            )}
            {me?.user.role === "admin" && (
              <Link to="/admin/feedback" className="settings-row">
                <Icon name="chat" />
                <span>
                  <strong>Feedback</strong>
                  <span className="muted">Problems, ideas and the asks the Director couldn't do yet</span>
                </span>
                <Icon name="back" size={16} />
              </Link>
            )}
            {me?.user.role === "admin" && (
              <Link to="/admin/showcase" className="settings-row">
                <Icon name="film" />
                <span>
                  <strong>Model showcase</strong>
                  <span className="muted">Make every video model's clips: status, plan, cap, start and stop</span>
                </span>
                <Icon name="back" size={16} />
              </Link>
            )}
            {me?.user.role === "admin" && (
              <Link to="/admin/services" className="settings-row">
                <Icon name="bolt" />
                <span>
                  <strong>Services</strong>
                  <span className="muted">Every API and service: up or not, this month's usage and spend, budgets</span>
                </span>
                <Icon name="back" size={16} />
              </Link>
            )}
            {me?.user.role === "admin" && (
              <Link to="/admin/costs" className="settings-row">
                <Icon name="sliders" />
                <span>
                  <strong>Costs</strong>
                  <span className="muted">Provider spend, what users paid, and the margin by kind of work</span>
                </span>
                <Icon name="back" size={16} />
              </Link>
            )}
            <button
              type="button"
              className="btn btn-quiet"
              data-action="account.sign-out"
              onClick={async () => {
                await api.logout().catch(() => undefined);
                director.stop("signout");
                set({ auth: "out", me: null });
                navigate("/login");
              }}
            >
              <Icon name="logout" size={18} /> Sign out
            </button>
          </section>
        </div>
      </div>
    </Shell>
  );
}

function VoicePicker({
  who,
  title,
  lede,
  current,
  currentNote,
  sampleText,
  asks,
  help,
}: {
  who: "director" | "narrator";
  title: string;
  lede: string;
  current: { voiceId: string; name: string } | null;
  currentNote?: string;
  sampleText: string;
  asks: string[];
  /** the data-help id how_do_i flashes (v4 D7) */
  help?: string;
}) {
  const [cards, setCards] = useState<Artifact[] | null>(null);
  const [asked, setAsked] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [sampling, setSampling] = useState(false);
  const [text, setText] = useState("");
  const playing = useStore((s) => s.playing === `cur-${who}`);
  const brand = useStore((s) => s.me?.channels[0]);

  const audition = async (description: string) => {
    setAsked(description);
    setLoading(true);
    const r = await tapTool("audition_voices", { for: who, description }, `asked to hear ${who} voices: "${description}"`, { keepCards: true });
    // Settings sets the default, not the voice of whatever ad was open last
    setCards(r ? r.cards.map((c) => ({ ...c, projectId: "", voiceFor: c.voiceFor ?? who, actions: c.actions?.length ? c.actions : ["use_voice"] })) : []);
    setLoading(false);
  };

  // the narrator default lives on the brand; the Director voice is the user's (set_director_voice switches live)
  const sample = async () => {
    if (!current) return;
    if (playing) return toggleAudio(`cur-${who}`, null);
    setSampling(true);
    const r = await tapTool("voice_sample", { voice_id: current.voiceId, text: sampleText }, `played a sample of ${current.name}`, { keepCards: true });
    setSampling(false);
    const url = r?.cards[0]?.url;
    if (url) toggleAudio(`cur-${who}`, url);
    else if (r) toast("No sample came back for that voice.", "bad");
  };

  return (
    <section className="group voice-picker" aria-labelledby={`${who}-h`} data-help={help}>
      <h2 id={`${who}-h`} className="group-title">
        {title}
      </h2>
      <p className="muted">{lede}</p>

      <div className="voice-current">
        <button data-action="voice.sample" type="button" className={`card-audio-btn voice-current-play${playing ? " is-on" : ""}`} onClick={() => void sample()} disabled={!current || sampling} aria-label={current ? (playing ? `Stop the sample of ${current.name}` : `Play a sample of ${current.name}`) : "No voice picked yet"}>
          <Icon name={playing ? "pause" : sampling ? "wave" : "play"} size={18} />
        </button>
        <span className="voice-current-text">
          <strong>{current?.name ?? "Not set"}</strong>
          <span className="muted">{currentNote ?? (sampling ? "Recording a sample" : "Tap to hear a sample")}</span>
        </span>
      </div>

      {who === "narrator" && !brand ? (
        <p className="muted">
          <Link to={channelSettingsPath(null)}>Set up your channel</Link> to keep a default narrator. You can still pick one per video.
        </p>
      ) : (
        <>
          <p className="field-label">Hear something different</p>
          <ul className="voice-asks" aria-label={`${title} ideas`}>
            {asks.map((a) => (
              <li key={a}>
                <button data-action="voice.audition" type="button" className={`chip${asked === a ? " is-on" : ""}`} onClick={() => void audition(a)} disabled={loading}>
                  {a}
                </button>
              </li>
            ))}
          </ul>
          <form
            className="composer voice-ask"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              if (text.trim()) void audition(text.trim());
            }}
          >
            <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Older, calmer, British…" aria-label={`Describe the ${who} voice you want`} enterKeyHint="search" />
            <button data-action="voice.audition" type="submit" className="icon-btn composer-send" aria-label="Find voices" disabled={!text.trim() || loading}>
              <Icon name="search" />
            </button>
          </form>
          {loading && <Spinner label="Finding the closest voices and recording samples" />}
          {!loading && cards && !cards.length && <p className="muted">No voices came back. Try different words.</p>}
          {!loading && !!cards?.length && (
            <ul className="voice-cards">
              {cards.map((c, i) => (
                <li key={`${c.voiceId}-${i}`}>
                  <ArtifactCard a={c} k={`aud-${who}-${c.voiceId}-${i}`} compact />
                </li>
              ))}
            </ul>
          )}
          <VoiceSearch for={who} />
        </>
      )}
    </section>
  );
}

/** v4.1 B5: the "Your video is ready" email (a render that took a while), on by default. */
function NotificationsPanel({ on }: { on: boolean }) {
  const [v, setV] = useState(on);
  const [busy, setBusy] = useState(false);
  return (
    <label className="check">
      <input
        type="checkbox"
        data-action="settings.notifications"
        checked={v}
        disabled={busy}
        onChange={async (e) => {
          const next = e.target.checked;
          setBusy(true);
          try {
            await call("PUT", "me/notifications", { videoReady: next });
            setV(next);
            const me = storeGet().me;
            if (me) set({ me: { ...me, user: { ...me.user, emailVideoReady: next } } });
            toast(next ? "You'll get an email when a video is ready." : "No more emails when a video is ready.", "good");
          } catch (err) {
            toast(`Couldn't save that. ${(err as Error).message}`, "bad");
          } finally {
            setBusy(false);
          }
        }}
      />{" "}
      Email me when a video I started is ready (renders that take more than a minute or two)
    </label>
  );
}
